import { httpRequest } from '@/apis/request'
import axios from 'axios'

/**
 * 将 Blob 或 File 进行 gzip 压缩
 * @param blob 要压缩的 Blob 或 File
 * @returns Promise<Blob> 压缩后的 Blob
 */
export async function compressGzip(blob) {
  // 使用浏览器原生的 CompressionStream API（现代浏览器支持）
  if ('CompressionStream' in window) {
    const stream = new CompressionStream('gzip')
    const compressedStream = blob.stream().pipeThrough(stream)
    return new Response(compressedStream).blob()
  }

  // 降级方案：如果浏览器不支持 CompressionStream，使用 pako
  // 注意：需要确保项目中已安装 pako，或者使用项目中已有的 pako
  try {
    // 动态导入 pako（如果可用）
    const pako = await import('pako')
    const arrayBuffer = await blob.arrayBuffer()
    const compressed = pako.gzip(new Uint8Array(arrayBuffer))
    return new Blob([compressed], { type: 'application/gzip' })
  } catch (error) {
    // 如果 pako 也不可用，返回原始 blob（不压缩）
    console.warn(
      'Gzip compression not available, returning original blob',
      error
    )
    return blob
  }
}

/**
 * 将 JSON 数据压缩为 gzip 格式的 Blob
 * @param data 要压缩的 JSON 数据
 * @returns Promise<Blob> 压缩后的 Blob
 */
export async function compressJsonToGzip(data) {
  const jsonString = typeof data === 'string' ? data : JSON.stringify(data)
  const blob = new Blob([jsonString], { type: 'application/json' })
  return compressGzip(blob)
}

/**
 * 检查 ArrayBuffer 是否是 gzip 压缩数据（gzip 魔数: 0x1f 0x8b）
 */
function isGzipBuffer(buffer) {
  if (!buffer || buffer.byteLength < 2) return false
  const view = new Uint8Array(
    buffer instanceof ArrayBuffer ? buffer : buffer.buffer
  )
  return view[0] === 0x1f && view[1] === 0x8b
}

/**
 * 解压 gzip 压缩的 Blob 或 ArrayBuffer
 * @param compressedData 压缩后的 Blob 或 ArrayBuffer
 * @returns Promise<Blob> 解压后的 Blob
 */
export async function decompressGzip(compressedData) {
  const arrayBuffer =
    compressedData instanceof Blob
      ? await compressedData.arrayBuffer()
      : compressedData

  // 浏览器可能已自动解压 gzip，直接返回
  if (!isGzipBuffer(arrayBuffer)) {
    return new Blob([arrayBuffer], { type: 'application/json' })
  }

  // 使用浏览器原生的 DecompressionStream API（现代浏览器支持）
  if ('DecompressionStream' in window) {
    const stream = new DecompressionStream('gzip')
    const blob = new Blob([arrayBuffer])
    const decompressedStream = blob.stream().pipeThrough(stream)
    return new Response(decompressedStream).blob()
  }

  // 降级方案：如果浏览器不支持 DecompressionStream，使用 pako
  try {
    const pako = await import('pako')
    const decompressed = pako.ungzip(new Uint8Array(arrayBuffer))
    return new Blob([decompressed], { type: 'application/json' })
  } catch (error) {
    console.error('Gzip decompression failed:', error)
    throw new Error('Gzip decompression not available')
  }
}

/**
 * 解压 gzip 压缩的数据并转换为 JSON 对象
 * @param compressedData 压缩后的 Blob 或 ArrayBuffer
 * @returns Promise<any> 解压后的 JSON 对象
 */
export async function decompressGzipToJson(compressedData) {
  const blob = await decompressGzip(compressedData)
  const text = await blob.text()
  return JSON.parse(text)
}

/**
 * 获取 gzip 压缩的数据
 * @param {string} url 数据 URL
 * @param {boolean} isData 是否返回数据对象
 * @returns {Promise} 返回解压后的数据
 */
export async function getGzipData(url, isData = true) {
  // 检查 URL 是否以 .gz 结尾，判断是否为 gzip 压缩文件
  const isGzip = url.endsWith('.gz') || url.includes('.gz?')

  if (isGzip) {
    // 如果是 gzip 压缩文件，使用 arraybuffer 获取数据后解压
    const response = await axios.get(url, {
      responseType: 'arraybuffer',
    })
    const decompressedData = await decompressGzipToJson(response.data)
    return isData ? { data: decompressedData } : decompressedData
  } else {
    // 如果不是压缩文件，正常获取
    return isData ? await axios.get(url) : await httpRequest.get(url)
  }
}
